import { RowData } from "../../../../types";
import {
  ChildCollection,
  ChildWorksheet,
  ExportContext,
  ParentExportRow,
} from "../../../../types/types.export";
import { CONDITIONAL_EXPORT_FIELDS } from "../../constants";
import { formatCellValue, getFieldDisplayValue } from "../exportRecordsHelpers";

export const buildChildSheets = (
  parentRows: ParentExportRow[],
  childCollections: ChildCollection[],
  context: ExportContext,
): ChildWorksheet[] => {
  return childCollections.map((collection) =>
    buildChildSheet(parentRows, collection, context),
  );
};

export const buildChildSheet = (
  parentRows: ParentExportRow[],
  childCollection: ChildCollection,
  context: ExportContext,
): ChildWorksheet => {
  const rows: RowData[] = [];

  /**
   * Parent Identifier -> First Excel Row
   */
  const firstRowMap = new Map<string, number>();

  parentRows.forEach(({ row: parent, identifier }) => {
    const children = parent[childCollection.field];

    if (!Array.isArray(children) || children.length === 0) {
      return;
    }

    children.forEach((child, index) => {
      if (!child || typeof child !== "object" || Array.isArray(child)) {
        return;
      }

      if (index === 0) {
        firstRowMap.set(identifier.key, rows.length + 2);
      }

      rows.push(
        buildChildRow(
          parent,
          identifier.fields,
          child,
          childCollection,
          context,
        ),
      );
    });
  });

  return {
    field: childCollection.field,
    sheetName: childCollection.title,
    rows,
    firstRowMap,
  };
};

export const buildChildRow = (
  parent: RowData,
  identifierFields: string[],
  child: RowData,
  childCollection: ChildCollection,
  context: ExportContext,
): RowData => {
  const row: RowData = {};

  const importMetadata = CONDITIONAL_EXPORT_FIELDS.find(({ condition }) =>
    condition(child),
  );

  importMetadata?.fields.forEach(({ key, title }) => {
    row[title] = formatCellValue(child[key]);
  });

  identifierFields.forEach((field) => {
    const property = childCollection.parentProperties[field];

    row[property?.title ?? field] = formatCellValue(parent[field]);
  });

  Object.entries(childCollection.properties).forEach(([field, property]) => {
    if (property?.isHidden) return;

    if (property.isGroupId || property.enum) {
      const displayValue = getFieldDisplayValue({
        value: child[field],
        fieldName: field,
        setupName: context.schema.title,
        property,
        iamUserState: context.iamUserState,
      });
      row[property.title] = formatCellValue(displayValue);
    } else {
      row[property.title] = formatCellValue(child[field]);
    }
  });

  return row;
};
